import './StaffDashboardView.css'

import { useEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { staff } from '../../store'

import { Button } from '../../components/Button'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { StatCard } from '../../components/StatCard'

export function StaffDashboardView() {
  const session = useAtomValue(staff.staffSessionAtom)
  const status = useAtomValue(staff.staffStatusAtom)
  const isLoading = useAtomValue(staff.staffStatusIsLoadingAtom)
  const error = useAtomValue(staff.staffStatusErrorAtom)
  const fetchStatus = useSetAtom(staff.fetchStaffStatusAtom)

  const canReadStatus = staff.hasStaffPermission(session, staff.Permission.StaffStatusRead)

  useEffect(() => {
    if (canReadStatus) fetchStatus()
  }, [canReadStatus, fetchStatus])

  if (!canReadStatus) {
    return (
      <div className="staff-dashboard">
        <PageHeader title="Dashboard" subtitle="Platform performance overview." />
        <EmptyState
          title="No status access"
          description="Your role does not include platform status access (staff.status.read)."
        />
      </div>
    )
  }

  return (
    <div className="staff-dashboard">
      <PageHeader
        title="Dashboard"
        subtitle="Live snapshot of how the platform is running."
        actions={
          <Button
            kind="secondary"
            text={isLoading ? 'Refreshing…' : 'Refresh'}
            onClick={() => fetchStatus()}
            disabled={isLoading}
          />
        }
      />

      {error && error !== 'forbidden' && (
        <EmptyState
          title="Could not load status"
          description={
            error === 'network' ? 'Network error — try again.' : 'The status request failed.'
          }
          actions={<Button kind="secondary" text="Retry" onClick={() => fetchStatus()} />}
        />
      )}

      {isLoading && !status && <p className="staff-dashboard__loading">Loading platform status…</p>}

      {status && (
        <div className="staff-dashboard__groups">
          <section className="staff-dashboard__group">
            <h2 className="staff-dashboard__group-title">Growth</h2>
            <div className="staff-dashboard__cards">
              <StatCard
                label="Users active"
                value={status.users.active.toLocaleString()}
                hint={`of ${status.users.total.toLocaleString()} total`}
              />
              <StatCard label="Users total" value={status.users.total.toLocaleString()} />
              <StatCard
                label="Profiles visible"
                value={status.profiles.visible.toLocaleString()}
                hint={`of ${status.profiles.total.toLocaleString()} total`}
              />
              <StatCard label="Profiles total" value={status.profiles.total.toLocaleString()} />
            </div>
          </section>

          <section className="staff-dashboard__group">
            <h2 className="staff-dashboard__group-title">Matching</h2>
            <div className="staff-dashboard__cards">
              <StatCard label="Matches active" value={status.matches.active.toLocaleString()} />
              <StatCard
                label="Matches connected"
                value={status.matches.connected.toLocaleString()}
              />
            </div>
          </section>

          <section className="staff-dashboard__group">
            <h2 className="staff-dashboard__group-title">Waitlist & regions</h2>
            <div className="staff-dashboard__cards">
              <StatCard label="Waitlist pending" value={status.waitlist.pending.toLocaleString()} />
              <StatCard
                label="Waitlist notified"
                value={status.waitlist.notified.toLocaleString()}
              />
              <StatCard
                label="Waitlist converted"
                value={status.waitlist.converted.toLocaleString()}
              />
              <StatCard
                label="Regions active"
                value={status.regions.active.toLocaleString()}
                hint={`${status.regions.inactive.toLocaleString()} inactive`}
              />
            </div>
          </section>

          <section className="staff-dashboard__group">
            <h2 className="staff-dashboard__group-title">Trust & safety</h2>
            <div className="staff-dashboard__cards">
              <StatCard
                label="Pending photos"
                value={status.moderation.pendingPhotos.toLocaleString()}
                href="/staff/moderation/photos"
              />
              <StatCard
                label="Open reports"
                value={status.moderation.openReports.toLocaleString()}
                href="/staff/moderation/reports"
              />
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
